
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { ApiResponseData, BaseService, SupportArticleService, ICountry, ICurrency, ISupportArticle, IPlatform, SanitizedResponse, ISupportCategory, SupportCategoryService } from "../../../../../services";
import { NavLink, useSearchParams } from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button, Dropdown, DropdownManageRef } from "@rivtn/noseur";
import { ArticleDialog } from "../../../../shared/ArticleDialog";

export interface ArticlesProps {
    platform: IPlatform;
}

function Articles(props: ArticlesProps) {
    const supportArticleService = SupportArticleService.getInstance();
    const supportCategoryService = SupportCategoryService.getInstance();
    const categoryDropdownManageRef = React.useRef<DropdownManageRef>(null);
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [supportCategories, setSupportCategories] = React.useState<ISupportCategory[]>();
    const labels = i18nManager.Labels.dashboard.platform_management.management.support.articles;
    const [supportArticles, setSupportArticles] = React.useState<ApiResponseData<ISupportArticle>>({});
    labels.sided_labels.dropdown_form![1].control.options = [{ label: i18nManager.Labels.common.all, value: "" }, ...i18nManager.Languages];

    React.useEffect(() => {
        supportCategoryService.queryCategories(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ISupportCategory>) => {
            setSupportCategories(apiResponse.data!.content);
            categoryDropdownManageRef.current?.changeOptions(apiResponse.data!.content?.map((c) => ({
                label: c.name,
                value: c.external_id,
            })));
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }, []);

    return SidedContainer({
        searchParams,
        setSearchParams,
        data: supportArticles,
        onAction: manageSupportArticle,
        queryData: fetchSupportArticles,
        labels: labels.sided_labels,
        firstStyle: {
            marginTop: 15,
            padding: "0px 20px 20px",
            border: "1px solid #F0F0F0",
        },
        secondStyle: {
            padding: 15,
            marginTop: 15,
            border: "1px solid #F0F0F0",
        },
        dataTemplate: (supportArticle: ISupportArticle) => {
            return (<div className="hera-item" onClick={() => {
                manageSupportArticle(supportArticle);
                Utils.updateSearchParams("adi", supportArticle.external_id, setSearchParams);
            }}>
                <div>
                    <div className="title lined-limit">
                        {supportArticle.title}
                        <span>
                            - {i18nManager.Languages.find((l) => l.value == supportArticle.language)?.label} ({supportArticle.language})
                            - {labels.helpful} {supportArticle.helpful}, {labels.not_helpful} {supportArticle.not_helpful}
                        </span>
                    </div>
                    <div className="content lined-limit">{supportArticle.content}</div>
                </div>
            </div>);
        },
        onSearchingInput: (name: string, manageRef?: any) => {
            if (name === "category") {
                categoryDropdownManageRef.current = manageRef;
                categoryDropdownManageRef.current?.changeOptions(supportCategories?.map((c) => ({
                    label: c.name,
                    value: c.external_id,
                })));
            }
        },
    } as any);

    function fetchSupportArticles() {
        supportArticleService.querySupportArticles(Utils.normalizeUrlParams(searchParams, { "category": "category.external_id" })).then(({ apiResponse }: SanitizedResponse<ISupportArticle>) => {
            setSupportArticles(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageSupportArticle(supportArticle: ISupportArticle = {} as any) {
        const isUpdate = !!supportArticle.title;
        const dialogLabels = labels.dialogs.add_edit;
        supportArticle.language = supportArticle.language ?? searchParams.get("language");

        const dialog = ArticleDialog({
            article: supportArticle,
            labels: {
                delete: dialogLabels.delete,
                title: dialogLabels.add_title,
                updateTitle: dialogLabels.edit_title,
                titlePlaceholder: dialogLabels.title,
                publish: dialogLabels.add_action_label,
                delete_desc: labels.dialogs.delete.desc,
                update: dialogLabels.update_action_label,
                delete_title: labels.dialogs.delete.title,
                contentPlaceholder: dialogLabels.content_in_markdown,
            },
            preComponents: (<>
                <Dropdown scheme={Schemes.RIVTN_QUIRINUS} placeholder={dialogLabels.select_lang}
                    selectedOptionIndex={i18nManager.Languages?.findIndex((c) => c?.value === supportArticle?.language)}
                    formControlProps={{ validStyle: { borderColor: "transparent" }, contentStyle: { borderRadius: 0 } }}
                    togglePosition={Alignment.LEFT} options={i18nManager.Languages}
                    onSelectOption={(option) => {
                        supportArticle.language = option.value;
                    }} />
                <Dropdown scheme={Schemes.RIVTN_QUIRINUS} placeholder={dialogLabels.search_and_select_a_cat}
                    selectedOptionIndex={(supportCategories?.findIndex((c) => c?.external_id === supportArticle?.category?.external_id))}
                    togglePosition={Alignment.LEFT} options={supportCategories} optionMap={{ label: "name", value: "external_id", }}
                    formControlProps={{ validStyle: { borderColor: "transparent" }, contentStyle: { borderRadius: 0 } }}
                    onSelectOption={(option) => {
                        supportArticle.category_id = option.external_id;
                    }} />
            </>),
            onAction: (article, button?: ButtonManageRef, dialogErrorRef?: React.RefObject<HTMLDivElement | null>) => {
                dialogErrorRef!.current!.innerText = " ";
                if (Object.keys(article).length < 4) {
                    dialogErrorRef!.current!.innerText = dialogLabels.fill_required_fields;
                    return false;
                }
                button?.setLoadingState(true);
                article.platform_id = props.platform.platform_id;
                const method = (isUpdate ? supportArticleService.updateSupportArticle : supportArticleService.createSupportArticle).bind(supportArticleService);
                method(article).then(({ }: SanitizedResponse<ISupportArticle>) => {
                    Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                    dialog.destroy(fetchSupportArticles);
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef!.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
            },
            onDelete: (article) => {
                dialog.hide(() => deleteSupportArticle(article));
            },
        });
        dialog.show();
    }

    function deleteSupportArticle(supportArticle: ISupportArticle) {
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialogs.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialogs.delete.desc.replace("${title}", supportArticle.title)
                .replace("${language}", supportArticle.language),
            onCancel: () => {
                dialog.hide(() => manageSupportArticle(supportArticle));
            },
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                supportArticleService.deleteSupportArticle(supportArticle.external_id).then(({ }: SanitizedResponse<any>) => {
                    Siren.alert(labels.dialogs.delete.success);
                    dialog.hide(fetchSupportArticles);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Articles;
